import {useCallback, useEffect, useState} from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {apiFetch} from '../api';
import TemplateCard from './TemplateCard';
import NewTemplateModal from './NewTemplateModal';
import NewChecklistModal from './NewChecklistModal';

function SortableTemplateCard({template, ...props}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: template.id});
    const {role, 'aria-disabled': _ariaDisabled, ...safeAttrs} = attributes;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className="sortable-template-wrapper" {...safeAttrs}>
            <TemplateCard data={template} isDragEnabled dragListeners={listeners} {...props} />
        </div>
    );
}

export default function TemplateContainer({onSpawn}) {
    const [templates, setTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [spawnTemplateId, setSpawnTemplateId] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklist-templates', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setTemplates(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        apiFetch('/checklist-groups', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setGroups(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, []);

    const handleCreate = useCallback(async (name, description) => {
        const res = await apiFetch('/checklist-templates', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name, description}),
        });
        const created = await res.json();
        setTemplates(prev => [...prev, created]);
        setShowModal(false);
    }, []);

    const handleUpdateTemplate = useCallback(async (id, name, description) => {
        const res = await apiFetch(`/checklist-templates/${id}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name, description}),
        });
        const updated = await res.json();
        setTemplates(prev => prev.map(t => t.id === id ? {...t, name: updated.name, description: updated.description} : t));
    }, []);

    const handleDelete = useCallback(async (id) => {
        await apiFetch(`/checklist-templates/${id}`, {method: 'DELETE'});
        setTemplates(prev => prev.filter(t => t.id !== id));
    }, []);

    const handleUse = useCallback((id) => {
        setSpawnTemplateId(id);
    }, []);

    const handleSpawnConfirm = useCallback(async (title, _templateId, groupId) => {
        setSpawnTemplateId(null);
        const res = await apiFetch(`/checklist-templates/${_templateId}/spawn`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name: title, groupId}),
        });
        const checklist = await res.json();
        if (onSpawn) onSpawn(checklist);
    }, [onSpawn]);

    const handleExpand = useCallback(async (id) => {
        const res = await apiFetch(`/checklist-templates/${id}`);
        return res.json();
    }, []);

    const handleAddItem = useCallback(async (templateId, text) => {
        const res = await apiFetch(`/checklist-templates/${templateId}/items`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({text}),
        });
        return res.json();
    }, []);

    const handleUpdateItem = useCallback(async (templateId, itemId, text) => {
        const res = await apiFetch(`/checklist-templates/${templateId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({text}),
        });
        return res.json();
    }, []);

    const handleReorderItems = useCallback((templateId, movedItemId, afterId) => {
        apiFetch(`/checklist-templates/${templateId}/items/${movedItemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        });
    }, []);

    const handleDeleteItem = useCallback(async (templateId, itemId) => {
        await apiFetch(`/checklist-templates/${templateId}/items/${itemId}`, {method: 'DELETE'});
    }, []);

    const reorderTemplates = useCallback((movedId, afterId, reordered) => {
        setTemplates(reordered);
        apiFetch(`/checklist-templates/${movedId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        });
    }, []);

    const templateSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    function handleTemplateDragEnd(event) {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = templates.findIndex(t => t.id === active.id);
        const newIndex = templates.findIndex(t => t.id === over.id);
        const reordered = arrayMove(templates, oldIndex, newIndex);
        const afterId = newIndex > 0 ? reordered[newIndex - 1].id : null;
        reorderTemplates(active.id, afterId, reordered);
    }

    return (
        <div className="checklist-container-root template-container">
            <div>
                <button className="add-checklist-button" onClick={() => setShowModal(true)}>
                    + New Template
                </button>
                {showModal && (
                    <NewTemplateModal onConfirm={handleCreate} onCancel={() => setShowModal(false)} />
                )}
                {spawnTemplateId && (
                    <NewChecklistModal
                        templates={templates}
                        groups={groups}
                        preselectedTemplateId={spawnTemplateId}
                        onConfirm={handleSpawnConfirm}
                        onCancel={() => setSpawnTemplateId(null)}
                    />
                )}
            </div>
            <DndContext sensors={templateSensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                <SortableContext items={templates.map(t => t.id)} strategy={horizontalListSortingStrategy}>
                    <div className="checklists-container">
                        {templates.map(template => (
                            <SortableTemplateCard
                                key={template.id}
                                template={template}
                                onUpdateTemplate={handleUpdateTemplate}
                                onDelete={handleDelete}
                                onUse={handleUse}
                                onExpand={handleExpand}
                                onAddItem={handleAddItem}
                                onUpdateItem={handleUpdateItem}
                                onReorderItems={handleReorderItems}
                                onDeleteItem={handleDeleteItem}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}
